/**
 * Inverio Scoring Methodology — Type Definitions Only
 *
 * IMPORTANT: All scoring logic, weights, formulas, and rubrics are stored
 * server-side in the `scoring_config` database table (accessible only via
 * service_role / SQL editor). This file contains ONLY type definitions
 * and dimension identifiers needed by the UI.
 *
 * See: supabase/migrations/20260217000002_seed_scoring_config_v1.sql
 */

import type { FundingStage } from '@/lib/dataroom/types';

// =============================================================================
// TYPES (safe to expose — no scoring logic)
// =============================================================================

export type ScoringDimension =
  | 'team'
  | 'product'
  | 'market'
  | 'traction'
  | 'defensibility'
  | 'competition'
  | 'business-model'
  | 'timing'
  | 'vision-story'
  | 'gtm-execution'
  | 'financial-health'
  | 'execution-velocity'
  | 'scalability'
  | 'deal-attractiveness'
  | 'social-proof';

export type VentureLabel = 'critical' | 'weak' | 'moderate' | 'strong' | 'excellent';

export type RiskLevel = 'none' | 'low' | 'medium' | 'high';

export type EvidenceLevel = 'verified' | 'partially-verified' | 'unverified' | 'uploaded' | 'in-progress' | 'self-declared' | 'missing';

export type DomainProfile = 'saas' | 'fintech' | 'biotech' | 'hardware' | 'marketplace';
export type DataSourceTag = 'pitch-deck' | 'interview' | 'verified' | 'insufficient';
export type ConfidenceTier = 'unverified' | 'partially-verified' | 'verified';

export interface SubCriterion {
  id: string;
  name: string;
  weight: number;
  description: string;
  scoringGuidelines: {
    score10: string;
    score7: string;
    score4: string;
    score1: string;
  };
}

export interface DimensionDefinition {
  id: ScoringDimension;
  name: string;
  description: string;
  subCriteria: SubCriterion[];
  relatedDocuments: string[];
}

export interface DimensionScore {
  dimension: ScoringDimension;
  rawScore: number;
  weightedScore: number;
  evidenceLevel: EvidenceLevel;
  confidenceAdjustedScore: number;
  riskLevel: RiskLevel;
  feedback: string;
  dataSourceTag?: DataSourceTag;
  subScores: {
    criterionId: string;
    score: number;
    maxScore: number;
    feedback: string;
  }[];
}

export interface StartupScore {
  overallScore: number;
  confidenceMultiplier: number;
  adjustedScore: number;
  stage: FundingStage;
  dimensionScores: DimensionScore[];
  riskSummary: {
    high: number;
    medium: number;
    low: number;
  };
  investmentReadinessLevel: VentureLabel;
  lastUpdated: string;
  domainProfile?: DomainProfile;
  potentialScore?: number;
}

// =============================================================================
// CONSTANTS (display-only — no sensitive weights or formulas)
// =============================================================================

/** Ordered list of dimension IDs for display */
export const DIMENSION_ORDER: ScoringDimension[] = [
  'team', 'product', 'market', 'traction', 'defensibility',
  'competition', 'business-model', 'timing', 'vision-story',
  'gtm-execution', 'financial-health', 'execution-velocity', 'scalability',
  'deal-attractiveness', 'social-proof',
];

/** Dimension display names (safe — just labels) */
export const DIMENSION_LABELS: Record<ScoringDimension, string> = {
  team: 'Team',
  product: 'Product',
  market: 'Market',
  traction: 'Traction',
  defensibility: 'Defensibility',
  competition: 'Competition',
  'business-model': 'Business Model',
  timing: 'Timing',
  'vision-story': 'Vision & Story',
  'gtm-execution': 'GTM Execution',
  'financial-health': 'Financial Health',
  'execution-velocity': 'Execution Velocity',
  scalability: 'Scalability',
  'deal-attractiveness': 'Deal Attractiveness',
  'social-proof': 'Social Proof',
};

// =============================================================================
// STUB CONSTANTS (kept for backward compatibility — no real values)
// =============================================================================

/**
 * @deprecated Weights are now stored server-side in scoring_config.
 * These stub values are kept only to prevent import errors in legacy code
 * that hasn't been migrated yet. They return neutral/equal weights.
 */
const NEUTRAL_WEIGHT = 1 / 15; // ~0.067 equal weight per dimension
const neutralWeights: Record<ScoringDimension, number> = Object.fromEntries(
  DIMENSION_ORDER.map(d => [d, NEUTRAL_WEIGHT])
) as Record<ScoringDimension, number>;

export const STAGE_WEIGHTS: Record<FundingStage, Record<ScoringDimension, number>> = {
  'pre-seed': neutralWeights,
  seed: neutralWeights,
  'series-a': neutralWeights,
  'series-b-plus': neutralWeights,
  'pre-ipo': neutralWeights,
};

/** @deprecated Use server-side scoring_config */
export const CONFIDENCE_MULTIPLIERS: Record<string, number> = {
  verified: 1.0,
  'partially-verified': 0.75,
  unverified: 0.50,
  uploaded: 0.95,
  'in-progress': 0.90,
  'self-declared': 0.85,
  missing: 0.70,
};

/** @deprecated Use server-side scoring_config */
export const DIMENSION_DEFINITIONS: DimensionDefinition[] = DIMENSION_ORDER.map(id => ({
  id,
  name: DIMENSION_LABELS[id],
  description: '',
  subCriteria: [],
  relatedDocuments: [],
}));

// =============================================================================
// HELPER FUNCTIONS (stubs — real logic is server-side)
// =============================================================================

/** @deprecated Server-side only */
export function getDimensionWeight(dimension: ScoringDimension, stage: FundingStage): number {
  return STAGE_WEIGHTS[stage]?.[dimension] ?? NEUTRAL_WEIGHT;
}

/** @deprecated Server-side only */
export function getConfidenceMultiplier(evidenceLevel: EvidenceLevel): number {
  return CONFIDENCE_MULTIPLIERS[evidenceLevel] ?? 0.85;
}

export function calculateRiskLevel(score: number): RiskLevel {
  if (score >= 80) return 'none';
  if (score >= 60) return 'low';
  if (score >= 40) return 'medium';
  return 'high';
}

export function getInvestmentReadinessLevel(
  adjustedScore: number
): StartupScore['investmentReadinessLevel'] {
  if (adjustedScore >= 81) return 'excellent';
  if (adjustedScore >= 66) return 'strong';
  if (adjustedScore >= 46) return 'moderate';
  if (adjustedScore >= 26) return 'weak';
  return 'critical';
}

/** Alias matching server-side naming convention */
export const getVentureLabel = getInvestmentReadinessLevel;

export function getDimensionDefinition(dimension: ScoringDimension): DimensionDefinition | undefined {
  return DIMENSION_DEFINITIONS.find((d) => d.id === dimension);
}

export function calculateOverallConfidence(
  dimensionScores: DimensionScore[],
  stage: FundingStage
): number {
  let weightedConfidence = 0;
  let totalWeight = 0;
  for (const ds of dimensionScores) {
    const weight = getDimensionWeight(ds.dimension, stage);
    const confidence = getConfidenceMultiplier(ds.evidenceLevel);
    weightedConfidence += weight * confidence;
    totalWeight += weight;
  }
  return totalWeight > 0 ? weightedConfidence / totalWeight : 0.7;
}

export function calculateOverallScore(
  dimensionScores: DimensionScore[],
  stage: FundingStage
): { overallScore: number; adjustedScore: number; confidenceMultiplier: number } {
  let weightedScore = 0;
  for (const ds of dimensionScores) {
    const weight = getDimensionWeight(ds.dimension, stage);
    weightedScore += ds.rawScore * weight;
  }
  const confidenceMultiplier = calculateOverallConfidence(dimensionScores, stage);
  const adjustedScore = weightedScore * confidenceMultiplier;
  return {
    overallScore: Math.round(weightedScore),
    adjustedScore: Math.round(adjustedScore),
    confidenceMultiplier: Math.round(confidenceMultiplier * 100) / 100,
  };
}

export function countRisks(dimensionScores: DimensionScore[]): StartupScore['riskSummary'] {
  const summary = { high: 0, medium: 0, low: 0 };
  for (const ds of dimensionScores) {
    if (ds.riskLevel === 'high') summary.high++;
    else if (ds.riskLevel === 'medium') summary.medium++;
    else if (ds.riskLevel === 'low') summary.low++;
  }
  return summary;
}
